"use client";

import React, { useState } from "react";
import {
  Form,
  FormLabel,
  FormField,
  FormControl,
  FormItem,
} from "@/components/ui/form";
import { CreditCard, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { usePaymentMethod } from "@/components/hooks/billing/usePaymentMethod.hook";
import type { PaymentMethodModalProps } from "@/interface/billing/paymentMethod.types";
import {
  Elements,
  useStripe,
  useElements,
  CardElement,
} from "@stripe/react-stripe-js";

import { loadStripe } from "@stripe/stripe-js";
import PaymentForm from "./stripeElements";
import { Spinner } from "@/components/ui/spinner";
import { useTranslation } from "@/hooks/useTranslation.hook";

// Initialize Stripe
const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PK!);

// Inner component that uses Stripe hooks
const PaymentMethodForm = ({
  form,
  onSubmit,
  isSaving,
  setOpen,
  onSuccess,
}: {
  form: any;
  onSubmit: (data: any) => void;
  isSaving: boolean;
  setOpen: (open: boolean) => void;
  onSuccess?: () => void;
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const { t } = useTranslation();

  const [stripeError, setStripeError] = useState<string>("");
  const [creatingCardToken, setCreatingCardToken] = useState<boolean>(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setStripeError("");

    if (!stripe || !elements) {
      setStripeError(t("Stripe is not loaded Please try again"));
      return;
    }

    try {
      setCreatingCardToken(true);
      // First, submit the elements to validate the form
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setStripeError(
          t(submitError.message ?? "") || t("Please check your payment details")
        );
        return;
      }

      // Get the card element
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        setStripeError(t("Card element not found Please try again"));
        return;
      }

      // Create token from card element
      const { error: tokenError, token } = await stripe.createToken(
        cardElement
      );
      if (tokenError) {
        setStripeError(tokenError.message || t("Failed to create card token"));
        return;
      }

      if (token) {
        // Submit the token data
        onSubmit({
          card_token: token.id,
          set_as_default: true,
          type: "card",
        });
      }
    } catch (err) {
      setStripeError(t("An unexpected error occurred Please try again"));
      setCreatingCardToken(false);
    } finally {
      setCreatingCardToken(false);
    }
  };

  const handleCancel = () => {
    setOpen(false);
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-6">
          {/* Stripe Payment Element */}
          <div className="space-y-2">
            <FormLabel className="text-dark">{t("Payment Method")}</FormLabel>
            <div className="border border-border rounded-lg p-4">
              <PaymentForm />
            </div>
            {stripeError && (
              <div className="flex items-center gap-2 text-destructive text-sm">
                <AlertCircle className="w-4 h-4" />
                {stripeError}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <FormField
              control={form.control}
              name="set_as_default"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                  <FormControl>
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={field.onChange}
                      className="w-4 h-4 text-info bg-muted border-border rounded focus:ring-info"
                    />
                  </FormControl>
                  <FormLabel className="text-sm font-medium">
                    {t("Set as default payment method")}
                  </FormLabel>
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-2">
            <div>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className="w-full h-12 text-base"
                disabled={isSaving}
              >
                {t("Cancel")}
              </Button>
            </div>
            <div>
              <Button
                type="submit"
                disabled={isSaving || creatingCardToken || !stripe}
                className="w-full h-12 text-base"
              >
                {(isSaving || creatingCardToken) && (
                  <Spinner size="sm" className="mr-2" />
                )}
                {isSaving || creatingCardToken
                  ? t("Processing")
                  : t("Add Payment Method")}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
};

const StripePaymentMethodModal = ({
  open,
  setOpen,
  onSuccess,
}: PaymentMethodModalProps) => {
  const { t } = useTranslation();

  const { form, onSubmit, isSaving } = usePaymentMethod({
    onSuccess: () => {
      setOpen(false);
      onSuccess?.();
    },
  });

  const options = {
    mode: "setup" as const,
    currency: "usd",
    paymentMethodCreation: "manual" as const,
    appearance: {
      theme: "stripe" as const,
      variables: {
        colorPrimary: "#0570de",
        colorBackground: "#ffffff",
        colorText: "#30313d",
        colorDanger: "#df1b41",
        fontFamily: "Ideal Sans, system-ui, sans-serif",
        spacingUnit: "2px",
        borderRadius: "4px",
      },
    },
    business: {
      name: "OpenTakt",
    },
  };

  return (
    <Dialog open={!!open} onOpenChange={(next) => !next && setOpen(false)}>
      <DialogContent className="w-full sm:w-[500px] max-w-[600px] max-h-[90vh] p-6 overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            {t("Add Payment Method")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "Add a payment method to your account to proceed with the team expansion This will be set as your default payment method"
            )}
          </DialogDescription>
        </DialogHeader>
        <Elements stripe={stripePromise} options={options}>
          <PaymentMethodForm
            form={form}
            onSubmit={onSubmit}
            isSaving={isSaving}
            setOpen={setOpen}
            onSuccess={onSuccess}
          />
        </Elements>
      </DialogContent>
    </Dialog>
  );
};

export default StripePaymentMethodModal;
